import { Asset } from 'types/asset';
import {
  CO2_KG_RECYCLED_BY_DEVICE_TYPE,
  CO2_KG_REUSED_BY_DEVICE_TYPE,
  DEFAULT_CO2_KG_RECYCLED,
  DEFAULT_CO2_KG_REUSED,
  SANITIZED_DD_STATUSES,
} from './sustainabilityReportConstants';

export type SustainabilityBucket = 'reused' | 'recycled' | 'landfill';

export interface SustainabilityAssetInput {
  quantity?: number | string | null;
  weight?: number | string | null;
  eolStatus?: string | null;
  status?: string | null;
  dataBearing?: string | null;
  ddStatus?: string | null;
  wipeStatus?: string | null;
  deviceType?: string | null;
}

export interface SustainabilityReportMetrics {
  totalAssetsProcessed: number;
  devicesReused: number;
  devicesRecycled: number;
  landfillDevices: number;
  totalWeightKg: number;
  dataSanitized: number;
  carbonSavedTonnes: number;
  chart: {
    reused: number;
    recycled: number;
    landfill: number;
  };
}

export function assetQuantity(asset: SustainabilityAssetInput): number {
  const parsed = parseInt(String(asset.quantity ?? '1'), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

export function assetWeightKg(asset: SustainabilityAssetInput): number {
  const parsed = parseFloat(String(asset.weight ?? '0'));
  return Number.isFinite(parsed) ? parsed : 0;
}

export function normalizedEol(asset: SustainabilityAssetInput): string {
  const raw = asset.eolStatus ?? asset.status ?? '';
  return String(raw).trim().toLowerCase();
}

export function isDataBearingYes(asset: SustainabilityAssetInput): boolean {
  return String(asset.dataBearing ?? '').trim().toLowerCase() === 'yes';
}

export function normalizedDdStatus(asset: SustainabilityAssetInput): string {
  const raw = asset.ddStatus ?? asset.wipeStatus ?? '';
  return String(raw).trim().toLowerCase();
}

function normalizeDeviceTypeKey(deviceType: string | null | undefined): string {
  return String(deviceType ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function co2KgForDevice(
  deviceType: string | null | undefined,
  bucket: SustainabilityBucket
): number {
  const key = normalizeDeviceTypeKey(deviceType);
  const map =
    bucket === 'reused'
      ? CO2_KG_REUSED_BY_DEVICE_TYPE
      : CO2_KG_RECYCLED_BY_DEVICE_TYPE;
  const fallback =
    bucket === 'reused' ? DEFAULT_CO2_KG_REUSED : DEFAULT_CO2_KG_RECYCLED;

  if (key && map[key] != null) {
    return map[key];
  }

  // Partial match (e.g. "Laptop Computer")
  const partialKey = Object.keys(map).find((k) => key.includes(k));
  if (partialKey) {
    return map[partialKey];
  }

  return fallback;
}

/**
 * Classify asset; chart landfill = total − reused − recycled (includes explicit landfill).
 */
export function classifySustainabilityBucket(
  asset: SustainabilityAssetInput
): SustainabilityBucket {
  const eol = normalizedEol(asset);

  if (eol.includes('reus')) {
    return 'reused';
  }
  if (eol.includes('recycl')) {
    return 'recycled';
  }
  if (eol.includes('landfill') || eol.includes('disposal')) {
    return 'landfill';
  }
  if (!eol && isDataBearingYes(asset)) {
    return 'reused';
  }
  return 'recycled';
}

export function isDataSanitized(asset: SustainabilityAssetInput): boolean {
  const dd = normalizedDdStatus(asset);
  if (dd && SANITIZED_DD_STATUSES.has(dd)) {
    return true;
  }

  const eol = normalizedEol(asset);
  const eolKnown =
    eol.includes('reus') ||
    eol.includes('recycl') ||
    eol.includes('landfill') ||
    eol.includes('disposal');

  if (!dd && !eolKnown && isDataBearingYes(asset)) {
    return true;
  }

  return false;
}

export function computeSustainabilityMetrics(
  assets: SustainabilityAssetInput[]
): SustainabilityReportMetrics {
  let totalAssetsProcessed = 0;
  let devicesReused = 0;
  let devicesRecycled = 0;
  let totalWeightKg = 0;
  let dataSanitized = 0;
  let carbonKg = 0;

  for (const asset of assets) {
    const qty = assetQuantity(asset);
    const bucket = classifySustainabilityBucket(asset);
    const weight = assetWeightKg(asset) * qty;

    totalAssetsProcessed += qty;
    totalWeightKg += weight;

    if (bucket === 'reused') {
      devicesReused += qty;
      carbonKg += qty * co2KgForDevice(asset.deviceType, 'reused');
    } else if (bucket === 'recycled') {
      devicesRecycled += qty;
      carbonKg += qty * co2KgForDevice(asset.deviceType, 'recycled');
    }

    if (isDataSanitized(asset)) {
      dataSanitized += qty;
    }
  }

  const landfillDevices = Math.max(
    0,
    totalAssetsProcessed - devicesReused - devicesRecycled
  );

  return {
    totalAssetsProcessed,
    devicesReused,
    devicesRecycled,
    landfillDevices,
    totalWeightKg: Math.round(totalWeightKg * 10) / 10,
    dataSanitized,
    carbonSavedTonnes: Math.round((carbonKg / 1000) * 100) / 100,
    chart: {
      reused: devicesReused,
      recycled: devicesRecycled,
      landfill: landfillDevices,
    },
  };
}

export function assetsToSustainabilityInput(
  assets: Asset[]
): SustainabilityAssetInput[] {
  return assets.map((a) => ({
    quantity: a.quantity,
    weight: a.weight,
    eolStatus: a.eolStatus,
    status: a.status,
    dataBearing: a.dataBearing,
    ddStatus: a.ddStatus,
    wipeStatus: a.wipeStatus,
    deviceType: a.deviceType,
  }));
}
